/**
 * Chasing overdue invoices — **the app does it, nobody does it by hand.**
 *
 * `/invoices` has always computed the queue: "5 to chase today", "$120,985
 * across 7 late invoices", three chips that reconcile against
 * `/api/invoices/summary` exactly. And then it waited for somebody to press a
 * button, every Monday, for ever. Harvest has sent late-payment reminders
 * automatically since 2009; this was the last manual step in a loop that
 * already had the message, the PDF, the audience, the hosted pay page and the
 * delivery log.
 *
 * `POST /api/cron/invoice-reminders` now fires hourly, every organization
 * chases on its own day and hour in its own zone, and the SERVER sends the
 * message through the same `deliverMany` the bulk-chase button uses — so an
 * automatic chase and a hand-pressed one are the same email, the same audience,
 * the same `InvoiceReminder` row. Everything below is a thin client over that.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

export interface ChaseSettings {
  enabled: boolean;
  /** ISO weekday: 1 = Monday … 7 = Sunday. */
  weekday: number;
  /** Local hour in `timeZone`, 0–23. */
  hour: number;
  timeZone: string;
  /** Days between chases on one invoice. */
  cadenceDays: number;
  /** Chases after which an invoice pauses and waits for a human. 0 = never. */
  stopAfter: number;
  defaults: {
    enabled: boolean;
    weekday: number;
    hour: number;
    timeZone: string;
    cadenceDays: number;
    stopAfter: number;
  };
}

/**
 * ONE AUTOMATIC ATTEMPT, READ HONESTLY.
 *
 * ⚠ `delivered` is `sent` or `filed` — the message reached the mailer. A
 * `refused`, a `failed` or a claim nobody resolved reached NOBODY, and the
 * screen must never spell it as a chase. That confusion is what let one broken
 * run report five invoices "already chased today" while $103,405 sat untouched.
 */
export interface ChaseAttempt {
  /** The org-local day the attempt belongs to. */
  day: string;
  at: string;
  outcome: string;
  to: string | null;
  /** The mailer's own sentence — what to fix. */
  reason: string | null;
  attempts: number;
  delivered: boolean;
  inFlight: boolean;
  failed: boolean;
  /** Out of automatic attempts for today; a person can still retry. */
  exhausted: boolean;
}

export interface ChaseQueueRow {
  id: string;
  number: string;
  clientId: string;
  clientName: string;
  dueAmount: number;
  dueDate: string;
  daysOverdue: number;
  chases: number;
  lastChaseAt: string | null;
  reason: 'never-chased' | 'follow-up';
  /** No billing address on the client at all — it will be skipped, and says so. */
  unreachable: boolean;
  /** A chase actually REACHED the client today. Not "a claim was written". */
  chasedToday: boolean;
  attempt: ChaseAttempt | null;
  attemptFailed: boolean;
  exhausted: boolean;
}

export interface ChasePausedRow {
  id: string;
  number: string;
  clientName: string;
  dueAmount: number;
  daysOverdue: number;
  chases: number;
}

/**
 * An invoice a PERSON took off the automatic queue — the per-invoice override.
 *
 * The org schedule is all-or-nothing, and "stop chasing everybody" is almost
 * never what an owner means: they mean *this* client is on a payment plan, or
 * disputing the scope, or already on the phone. Listed here with who, when and
 * why, because a queue that silently shrinks is a queue nobody trusts.
 */
export interface ChaseHeldRow {
  id: string;
  number: string;
  clientName: string;
  dueAmount: number;
  daysOverdue: number;
  chases: number;
  reason: string | null;
  at: string;
  by: string | null;
}

export interface ChaseOverview {
  settings: ChaseSettings;
  /** "Monday 9:00 AM" — computed by the same function the cron obeys. */
  scheduleLabel: string;
  /** "every 7 days, stopping after 4". */
  cadenceLabel: string;
  /** Automatic tries per invoice per day before the machine waits for a person. */
  maxAttemptsPerDay: number;
  nextRunAt: string | null;
  lastRunAt: string | null;
  /**
   * Delivery reality. In development `mode` is `file`: messages are written to
   * `.mail/` and nothing is transmitted, and the bar says so BEFORE anything is
   * pressed — the same honesty rule the composer carries.
   */
  email: { mode: 'live' | 'file' | 'off'; reason: string | null };
  queue: ChaseQueueRow[];
  paused: ChasePausedRow[];
  /** Held by a human — see `ChaseHeldRow`. */
  held: ChaseHeldRow[];
  counts: {
    queued: number;
    sendable: number;
    /** DELIVERED today. Structurally 0 while a row is still queued. */
    chasedToday: number;
    /** Attempted today and nothing reached the client — still queued. */
    failedToday: number;
    exhausted: number;
    inFlight: number;
    unreachable: number;
    neverChased: number;
    followUp: number;
    paused: number;
    held: number;
  };
  /** The most recent failure's own words — what a person has to fix. */
  lastFailureReason: string | null;
  amounts: {
    queued: number;
    sendable: number;
    failedToday: number;
    paused: number;
    held: number;
  };
}

export interface ChaseRunResult {
  ran: boolean;
  reason?: string;
  /**
   * The reason as a token rather than prose. `contended` means another run
   * holds these invoices RIGHT NOW — the bar must not answer that with "still
   * queued", which reads as "press it again" and is how one client ends up
   * with two dunning letters.
   */
  reasonCode?: 'disabled' | 'not-send-hour' | 'nothing-due' | 'retry-spent' | 'chased-today' | 'contended';
  chased: number;
  skipped: number;
  failed: number;
  amount: number;
  messages: number;
  mode: string;
  overview: ChaseOverview;
}

export function useChaseReminders() {
  return useQuery({
    queryKey: ['invoices', 'chase-reminders'],
    queryFn: () => api.get<ChaseOverview>('/invoices/reminders'),
    retry: false,
  });
}

export function useUpdateChaseReminders() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Omit<ChaseSettings, 'defaults'>>) =>
      api.patch<ChaseOverview>('/invoices/reminders', patch),
    // The cadence is not decoration: changing it moves the "due to follow up"
    // queue, every row's next-follow-up date and the chips above the table, so
    // the whole invoices tree is refetched rather than this one card.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['invoices'] }),
  });
}

/**
 * Run the chase now instead of waiting for the schedule. It calls the same
 * service the cron calls — a way of not waiting for Monday, never a second
 * sending path.
 */
export function useRunChaseNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids?: string[] } = {}) =>
      api.post<ChaseRunResult>('/invoices/reminders/run', input),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['invoices'] }),
  });
}

/**
 * HOLD ONE INVOICE, OR RELEASE IT — the per-invoice override.
 *
 * The whole invoices tree is invalidated for the same reason the cadence is:
 * a hold moves the queue, the chips above the table and the row's own state,
 * so refetching just this card would leave the screen disagreeing with itself.
 */
export function useSetChaseHold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, held, reason }: { id: string; held: boolean; reason?: string | null }) =>
      api.patch<ChaseOverview>(`/invoices/${id}/reminders/hold`, { held, reason }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['invoices'] }),
  });
}

// ---------------------------------------------------------------------------
// Wording
// ---------------------------------------------------------------------------

export const WEEKDAY_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
];

export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: String(h),
  label: `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? 'AM' : 'PM'}`,
}));

/** Days between chases. Weekly is the default; the rest are the real answers. */
export const CADENCE_OPTIONS = [3, 5, 7, 10, 14, 21, 30].map((d) => ({
  value: String(d),
  label: `Every ${d} days`,
}));

/** How many times one invoice is chased before it waits for a human. */
export const STOP_AFTER_OPTIONS = [
  { value: '0', label: 'Never stop' },
  ...[2, 3, 4, 5, 6, 8].map((n) => ({ value: String(n), label: `After ${n} chases` })),
];

/**
 * "Mon, Aug 17" in the ORGANIZATION's zone, never the browser's.
 *
 * The DAY only: the sentence has already said "Monday 9:00 AM", and repeating
 * the hour two words later ("Monday 9:00 AM — next Mon, Aug 17, 9:00 AM") is
 * the same fact twice at the cost of a second line on the busiest screen in the
 * app.
 */
export function formatNextRun(iso: string | null, timeZone: string): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  try {
    return at.toLocaleString('en-US', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return at.toLocaleDateString();
  }
}

/**
 * The mailer's own sentence, made safe to drop into the middle of ours.
 *
 * Reasons arrive already punctuated ("No address to send to.") and were being
 * interpolated in front of our own full stop — "No address to send to..".
 * Trimmed here, in one place, so the two screens that print a reason cannot
 * disagree about it.
 */
export function chaseReasonPhrase(reason: string | null | undefined): string | null {
  const text = (reason ?? '').trim().replace(/[.\s]+$/, '');
  return text || null;
}
